import { resolvePortalMessage, type PortalMessage } from "../../../i18n/message.js";
import { portalText, usePortalTranslation, portalLocale } from "../../../i18n/portal.js";
/**
 * `Organization` → Finance → Documents — the "Legal and tax details" form (plan-014 D5a; design §27.15.1
 * `LegalProfileForm`; FR12-BIL-01). Drawn to the prototype's `docs` page (`design/WeTalk.dc.html`, the Organization
 * block renderer): a block head — "Legal and tax details", "Printed on every new document. Documents already issued
 * keep the details they were issued with.", Save — over the form grid (label, input, hint under each), with the
 * prototype's fields: Legal name, VAT number ("Checked with VIES on 12 Sep — valid."), Address, Billing emails,
 * Accounting reference ("Optional. Shown on invoices."), Currency ("EUR — All billing is in euros."). The design adds
 * the payer kind and the parts of the address an invoice needs (country, postal code, city), and states the legal
 * payer and the scope it covers (every balance and workspace of the organization).
 *
 * D6 owns the page (`financial-document-screen.tsx`) and mounts this block above the documents table with the
 * organization's id and name; this module owns the form and nothing else.
 *
 * EVERY STATE IS REAL (FR12-SCR-04): loading, empty ("No legal details yet"), the API's refusal under the field it
 * names (`tax_id_malformed` under the VAT number), a save in flight, a save over someone else's (`revision_stale` —
 * reload them), and the outcome where the prototype puts its toast. There is no "Check again" (owner answer 42,
 * 2026-10-06): the number is checked once when it is entered or changed, and a recheck is WeTalk support's, from the
 * staff portal. The server decides everything: the shape and country rules, the check, the VAT treatment; this form
 * only shows what it answered.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import { SUPPORTED_COUNTRY } from "@wetalk/domain";
import { Button, Field, Icon, Input, Select } from "@wetalk/ui";
import { useState, type FormEvent, type ReactNode } from "react";

import { apiProblemText } from "../../../api/api-error.js";
import { useGateway } from "../../../api/provider.js";
import type {
  BillingProfileInput,
  BillingProfileView,
  PayerKindView,
  VatTreatmentView,
} from "../../../api/slice/billing-profile.js";
import { GLYPH } from "../../../shell/glyph.js";

import "./legal-profile-form.css";

export interface LegalProfileFormProps {
  /** The organization whose details these are (`account_id`). */
  readonly accountId: string;
  /** Its name, for the sentence that states the legal payer and the scope. */
  readonly organizationName: string;
}

/** This form's query key (one set of details per organization). */
export function legalProfileQueryKey(accountId: string): readonly ["billing-profile", string] {
  return ["billing-profile", accountId];
}

// ── copy ──────────────────────────────────────────────────────────────────────────────────────────────────────

export const LEGAL_PROFILE_COPY = {
  get title() { return portalText("finance", "m1227"); },
  get dek() { return portalText("finance", "m1228"); },
  get saved() { return portalText("finance", "m1229"); },
  get empty() { return portalText("finance", "m1230"); },
  get stale() { return portalText("finance", "m1231"); },
} as const;

function countryNameOf(code: string): string {
  try {
    return new Intl.DisplayNames([portalLocale()], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** The prototype's "12 Sep" (three letters always: ICU's en-GB now says "Sept"), in the reader's own calendar day. */
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

function dayMonthOf(iso: string): string {
  const at = new Date(iso);
  if (portalLocale() === "el") return new Intl.DateTimeFormat("el-GR", { day: "numeric", month: "short" }).format(at);
  return `${String(at.getDate())} ${MONTH_SHORT[at.getMonth()] ?? ""}`;
}

/** The hint under the VAT number: what the EU VAT register said, in the prototype's words. */
export function taxIdHintOf(profile: BillingProfileView | null, payerKind: PayerKindView, country: string, typed: string): string {
  if (payerKind === "consumer") return portalText("finance", "m0464");
  if (country !== "" && !isEuCountry(country)) return portalText("finance", "m0465");
  const compact = typed.replace(/[\s.-]/g, "").toUpperCase();
  const sameNumber = profile !== null && profile.taxId !== null && compact !== "" && profile.taxId.endsWith(compact) && profile.country === country;
  if (profile === null || !profile.taxIdChecked || !sameNumber) return portalText("finance", "m0466");
  const on = profile.checkedAt === null ? null : dayMonthOf(profile.checkedAt);
  switch (profile.taxIdStatus) {
    case "valid":
      return on === null ? portalText("finance", "m0467") : portalText("finance", "m0468", { value1: on });
    case "invalid":
      return on === null ? portalText("finance", "m0469") : portalText("finance", "m0470", { value1: on });
    case "unavailable":
      return on === null
        ? portalText("finance", "m0471")
        : portalText("finance", "m0472", { value1: on });
    case "pending":
      return portalText("finance", "m0473");
    case "unchecked":
      return portalText("finance", "m0466");
  }
}

/** One sentence on how VAT applies to the next document — the server's decision, never the form's. */
export function vatTreatmentTextOf(treatment: VatTreatmentView): string {
  switch (treatment.kind) {
    case "domestic_greece":
      return portalText("finance", "m0474");
    case "reverse_charge":
      return portalText("finance", "m0475");
    case "outside_scope":
      return portalText("finance", "m0476");
    case "destination_consumer":
      return treatment.reason === "consumer"
        ? portalText("finance", "m0477", { value1: countryNameOf(treatment.country) })
        : portalText("finance", "m0478", { value1: countryNameOf(treatment.country) });
  }
}

/** The outcome of a save, in words: the prototype's sentence, plus what the register said about a new number. */
export function savedTextOf(profile: BillingProfileView): string {
  if (!profile.taxIdChecked) return LEGAL_PROFILE_COPY.saved;
  if (profile.taxIdStatus === "invalid") return portalText("finance", "m0479", { value1: LEGAL_PROFILE_COPY.saved });
  if (profile.taxIdStatus === "unavailable" || profile.taxIdStatus === "pending") {
    return portalText("finance", "m0480", { value1: LEGAL_PROFILE_COPY.saved });
  }
  return LEGAL_PROFILE_COPY.saved;
}

/** The EU member states, for the hint only — the server applies the rule. */
const EU_COUNTRY: ReadonlySet<string> = new Set([
  "AT", "BE", "BG", "CY", "CZ", "DE", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IT", "LT", "LU", "LV",
  "MT", "NL", "PL", "PT", "RO", "SE", "SI", "SK",
]);

function isEuCountry(code: string): boolean {
  return EU_COUNTRY.has(code);
}

// ── the draft ─────────────────────────────────────────────────────────────────────────────────────────────────

interface Draft {
  readonly payerKind: PayerKindView;
  readonly legalName: string;
  readonly tradingName: string;
  readonly taxId: string;
  readonly country: string;
  readonly address: string;
  readonly postalCode: string;
  readonly city: string;
  readonly billingEmail: string;
  readonly accountingReference: string;
}

const BLANK: Draft = {
  payerKind: "business",
  legalName: "",
  tradingName: "",
  taxId: "",
  country: "",
  address: "",
  postalCode: "",
  city: "",
  billingEmail: "",
  accountingReference: "",
};

function draftOf(profile: BillingProfileView | null): Draft {
  if (profile === null) return BLANK;
  return {
    payerKind: profile.payerKind,
    legalName: profile.legalName,
    tradingName: profile.tradingName ?? "",
    taxId: profile.taxId ?? "",
    country: profile.country,
    address: profile.addressLine.join(", "),
    postalCode: profile.postalCode ?? "",
    city: profile.city,
    billingEmail: profile.billingEmail.join(", "),
    accountingReference: profile.accountingReference ?? "",
  };
}

function orNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

/** The request: the address kept line by line when untouched, the recipients split on commas. */
export function inputOf(draft: Draft, profile: BillingProfileView | null): BillingProfileInput {
  const untouchedAddress = profile !== null && draft.address === profile.addressLine.join(", ");
  return {
    legalName: draft.legalName,
    tradingName: orNull(draft.tradingName),
    payerKind: draft.payerKind,
    country: draft.country,
    addressLine: untouchedAddress ? profile.addressLine : [draft.address],
    postalCode: orNull(draft.postalCode),
    city: draft.city,
    taxId: orNull(draft.taxId),
    billingEmail: draft.billingEmail
      .split(/[,;\s]+/)
      .map((one) => one.trim())
      .filter((one) => one !== ""),
    accountingReference: orNull(draft.accountingReference),
    expectedBillingProfileId: profile?.billingProfileId ?? null,
  };
}

/** The API's field names → this form's fields, so a refusal shows under the field it names. */
const FIELD_OF: Readonly<Record<string, keyof Draft>> = {
  legal_name: "legalName",
  trading_name: "tradingName",
  tax_id: "taxId",
  country: "country",
  address_line: "address",
  postal_code: "postalCode",
  city: "city",
  billing_email: "billingEmail",
  accounting_reference: "accountingReference",
  payer_kind: "payerKind",
};

interface Toast {
  readonly tone: "done" | "problem";
  readonly text: PortalMessage;
}

function countryOptionsWith(selected: string): readonly { readonly code: string; readonly name: string }[] {
  const codes: readonly string[] = selected !== "" && !SUPPORTED_COUNTRY.includes(selected as never) ? [...SUPPORTED_COUNTRY, selected] : SUPPORTED_COUNTRY;
  return codes.map((code) => ({ code, name: countryNameOf(code) })).sort((left, right) => left.name.localeCompare(right.name, portalLocale()));
}

// ── the block ─────────────────────────────────────────────────────────────────────────────────────────────────

export function LegalProfileForm(props: LegalProfileFormProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const key = legalProfileQueryKey(props.accountId);
  const read = useQuery({ queryKey: key, queryFn: () => gateway.readBillingProfile(props.accountId) });
  // Bumped after a reload (`revision_stale`), so the editor starts a fresh draft from what is stored now.
  const [generation, setGeneration] = useState(0);

  if (read.isPending) {
    return (
      <div className="lp-block">
        <BlockHead />
        <p className="c-note" role="status">{portalText("finance", "m0481")}</p>
      </div>
    );
  }
  if (read.isError) {
    return (
      <div className="lp-block">
        <BlockHead />
        <p className="lp-toast lp-toast-problem" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="xl" />
          <span>{apiProblemText(read.error, portalText("finance", "m0482"))}</span>
        </p>
        <div className="lp-actions">
          <Button variant="secondary" onClick={() => void read.refetch()}>{portalText("finance", "m0382")}</Button>
        </div>
      </div>
    );
  }
  return (
    <LegalProfileEditor
      key={generation}
      {...props}
      profile={read.data}
      onSaved={(next) => queryClient.setQueryData(key, next)}
      onReload={() => {
        void queryClient.invalidateQueries({ queryKey: key }).then(() => {
          setGeneration((current) => current + 1);
        });
      }}
    />
  );
}

function BlockHead(props: { readonly children?: ReactNode }): ReactNode {
  usePortalTranslation("finance");
  return (
    <div className="lp-block-head">
      <div className="lp-block-heading">
        <h2 className="lp-block-title">{LEGAL_PROFILE_COPY.title}</h2>
        <p className="lp-block-dek">{LEGAL_PROFILE_COPY.dek}</p>
      </div>
      {props.children === undefined ? null : <div className="lp-block-buttons">{props.children}</div>}
    </div>
  );
}

function LegalProfileEditor(
  props: LegalProfileFormProps & {
    readonly profile: BillingProfileView | null;
    readonly onSaved: (next: BillingProfileView) => void;
    readonly onReload: () => void;
  },
): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const { profile } = props;
  const [draft, setDraft] = useState<Draft>(() => draftOf(profile));
  const [fieldError, setFieldError] = useState<Partial<Record<keyof Draft, PortalMessage>>>({});
  const [toast, setToast] = useState<Toast | null>(null);
  const [stale, setStale] = useState(false);

  const set = (field: keyof Draft) => (value: string) => {
    setDraft((current) => ({ ...current, [field]: value }));
    setFieldError((current) => ({ ...current, [field]: undefined }));
  };

  const save = useMutation({
    mutationFn: () => gateway.saveBillingProfile(props.accountId, inputOf(draft, profile)),
    onSuccess: (next) => {
      setToast({ tone: next.taxIdStatus === "invalid" ? "problem" : "done", text: () => savedTextOf(next) });
      setDraft(draftOf(next));
      props.onSaved(next);
    },
    onError: (raised: Error) => {
      if (raised instanceof ApiError && raised.code === "revision_stale") {
        setStale(true);
        return;
      }
      const field = raised instanceof ApiError && raised.field !== null ? FIELD_OF[raised.field] : undefined;
      const text = () => apiProblemText(raised, portalText("finance", "m0483"));
      if (field !== undefined) setFieldError({ [field]: text });
      else setToast({ tone: "problem", text });
    },
  });

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (save.isPending) return;
    setToast(null);
    save.mutate();
  };

  const companyPayer = draft.payerKind === "business";
  const describedError = (field: keyof Draft): { readonly error: string } | Record<string, never> =>
    fieldError[field] === undefined ? {} : { error: resolvePortalMessage(fieldError[field]) };

  return (
    <form className="lp-block" onSubmit={submit} aria-label={LEGAL_PROFILE_COPY.title} noValidate>
      <BlockHead>
        <Button variant="secondary" type="submit" disabled={save.isPending || stale}>
          {save.isPending ? portalText("finance", "m0484") : portalText("finance", "m0485")}
        </Button>
      </BlockHead>

      {toast === null ? null : (
        <p className={toast.tone === "done" ? "lp-toast" : "lp-toast lp-toast-problem"} role="status">
          <Icon glyph={toast.tone === "done" ? GLYPH.checkCircle : GLYPH.warningCircle} size="xl" />
          <span>{resolvePortalMessage(toast.text)}</span>
        </p>
      )}
      {stale ? (
        <div className="lp-toast lp-toast-problem" role="alert">
          <Icon glyph={GLYPH.warningCircle} size="xl" />
          <span className="lp-toast-body">{LEGAL_PROFILE_COPY.stale}</span>
          <Button variant="secondary" onClick={props.onReload}>{portalText("finance", "m0486")}</Button>
        </div>
      ) : null}

      <p className="lp-scope">
        {profile === null
          ? LEGAL_PROFILE_COPY.empty
          : portalText("finance", "m0487", { value1: profile.legalName, value2: props.organizationName, value3: dayMonthOf(profile.appliesFrom) })}
      </p>

      <div className="lp-grid">
        <Field label={portalText("finance", "m0488")} description={companyPayer ? portalText("finance", "m0489") : portalText("finance", "m0490")} {...describedError("payerKind")}>
          {(control) => (
            <Select {...control} value={draft.payerKind} onChange={(event) => set("payerKind")(event.currentTarget.value === "consumer" ? "consumer" : "business")}>
              <option value="business">{portalText("finance", "m0492")}</option>
              <option value="consumer">{portalText("finance", "m0493")}</option>
            </Select>
          )}
        </Field>
        <Field label={portalText("finance", "m0494")} {...describedError("legalName")}>
          {(control) => <Input {...control} value={draft.legalName} onChange={(event) => set("legalName")(event.currentTarget.value)} autoComplete="organization" />}
        </Field>
        <Field label={portalText("finance", "m0496")} description={portalText("finance", "m0497")} {...describedError("tradingName")}>
          {(control) => <Input {...control} value={draft.tradingName} onChange={(event) => set("tradingName")(event.currentTarget.value)} />}
        </Field>
        <Field label={companyPayer ? portalText("finance", "m0499") : portalText("finance", "m0500")} description={taxIdHintOf(profile, draft.payerKind, draft.country, draft.taxId)} {...describedError("taxId")}>
          {(control) => <Input {...control} value={draft.taxId} onChange={(event) => set("taxId")(event.currentTarget.value)} font="mono" />}
        </Field>
        <Field label={portalText("finance", "m0502")} {...describedError("country")}>
          {(control) => (
            <Select {...control} value={draft.country} onChange={(event) => set("country")(event.currentTarget.value)}>
              <option value="" disabled>{portalText("finance", "m0503")}</option>
              {countryOptionsWith(draft.country).map((one) => (
                <option key={one.code} value={one.code}>
                  {one.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={portalText("finance", "m0066")} {...describedError("address")}>
          {(control) => <Input {...control} value={draft.address} onChange={(event) => set("address")(event.currentTarget.value)} autoComplete="street-address" />}
        </Field>
        <Field label={portalText("finance", "m0504")} {...describedError("postalCode")}>
          {(control) => <Input {...control} value={draft.postalCode} onChange={(event) => set("postalCode")(event.currentTarget.value)} autoComplete="postal-code" />}
        </Field>
        <Field label={portalText("finance", "m0506")} {...describedError("city")}>
          {(control) => <Input {...control} value={draft.city} onChange={(event) => set("city")(event.currentTarget.value)} autoComplete="address-level2" />}
        </Field>
        <Field label={portalText("finance", "m0507")} description={portalText("finance", "m0508")} {...describedError("billingEmail")}>
          {(control) => <Input {...control} value={draft.billingEmail} onChange={(event) => set("billingEmail")(event.currentTarget.value)} />}
        </Field>
        <Field label={portalText("finance", "m0510")} description={portalText("finance", "m0511")} {...describedError("accountingReference")}>
          {(control) => <Input {...control} value={draft.accountingReference} onChange={(event) => set("accountingReference")(event.currentTarget.value)} />}
        </Field>
        <Field label={portalText("finance", "m0513")} description={portalText("finance", "m0514")}>
          {(control) => <Input {...control} value="EUR" readOnly />}
        </Field>
      </div>

      {profile === null ? null : <p className="lp-vat">{vatTreatmentTextOf(profile.vatTreatment)}</p>}
    </form>
  );
}
